// Utilidades del buscador de órdenes (sin dependencias de Firebase para poder probarlas aisladas)

export const MIN_REMOTE_SEARCH_LENGTH = 3;

// Minúsculas, sin tildes ni espacios extra: "José  Pérez" -> "jose perez"
export const normalizeText = (value: any): string =>
    String(value ?? "")
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .toLowerCase()
        .replace(/\s+/g, " ")
        .trim();

// Separa el término en palabras; el "#" del número de orden se ignora
export const tokenizeSearch = (term: string): string[] =>
    normalizeText(term.replace(/#/g, " ")).split(" ").filter(Boolean);

export const getOrderLocation = (item: any): string =>
    item.city || item.street || (typeof item.payToCode === "string" ? item.payToCode : item.payToCode?.location) || "";

export type TipoPedido = "NORMAL" | "CONSIGNACION_EVENT" | "CONSIGNACION_PERM";

export const TIPO_PEDIDO_LABEL: Record<TipoPedido, string> = {
    NORMAL: "Orden Normal",
    CONSIGNACION_EVENT: "Consignación Eventual",
    CONSIGNACION_PERM: "Consignación Permanente"
};

// Las órdenes creadas antes de la clasificación no traen tipoPedido y se consideran normales
export const resolveTipoPedido = (item: any): TipoPedido => {
    if (item.tipoPedido === "CONSIGNACION_EVENT" || item.tipoPedido === "CONSIGNACION_PERM") return item.tipoPedido;
    if (item.tipoPedido === "CONSIGNACION") return item.subtipoConsignacion === "PERMANENTE" ? "CONSIGNACION_PERM" : "CONSIGNACION_EVENT";
    return "NORMAL";
};

// Todas las palabras deben aparecer en alguno de los campos, en cualquier orden.
// Incluye el tipo de pedido para poder escribir "consignación", "eventual" o "permanente".
export const matchesSearch = (item: any, tokens: string[]): boolean => {
    if (tokens.length === 0) return true;
    const haystack = normalizeText([
        item.cardName,
        item.cardFName,
        item.cardCode,
        item.vendorName,
        item.numberOrder,
        getOrderLocation(item),
        TIPO_PEDIDO_LABEL[resolveTipoPedido(item)]
    ].join(" "));
    return tokens.every((token) => haystack.includes(token));
};

const toTitleCase = (value: string): string =>
    value.toLowerCase().replace(/(^|\s)\S/g, (char) => char.toUpperCase());

export interface RemoteSearchPlan {
    numberOrder: number | null;
    codePrefix: string;
    namePrefixes: string[];
}

// Variantes para consultar Firestore fuera de las órdenes cargadas.
// Firestore solo permite igualdad o prefijo y distingue mayúsculas, por eso se prueban varias formas del texto.
export const buildRemoteSearchPlan = (term: string): RemoteSearchPlan | null => {
    const clean = term.replace(/#/g, " ").replace(/\s+/g, " ").trim();
    if (clean.length < MIN_REMOTE_SEARCH_LENGTH) return null;

    const upper = clean.toUpperCase();
    return {
        numberOrder: /^\d+$/.test(clean) ? Number(clean) : null,
        codePrefix: upper,
        namePrefixes: Array.from(new Set([upper, clean, toTitleCase(clean)]))
    };
};
